import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Radio, X } from "lucide-react";
import {
  Button,
  Callout,
  ColumnsMenu,
  EmptyState,
  Grid,
  GridWrap,
  IconButton,
  LinkButton,
  SegmentedFilter,
  SkeletonRows,
  TabList,
  TabPanel,
  formatRelative,
  errorText,
  Switch,
  useColumnVisibility,
  type ColumnVisibility,
} from "@amber/ui";
import {
  dismissedBannersSchema,
  type SourceBulkAction,
  type SourceValues,
  type SourcesResponse,
} from "../../contracts/index";
import { AddSourcePanel } from "./AddSource";
import { sourcesQuery, sourceValuesQuery } from "../queries";
import { EditSourcePanel } from "./EditSource";
import type { SourcesSearch } from "../search";
import { SourceDrawer } from "./SourceDrawer";
import { GapsTable } from "./GapsTable";
import { PlatformsTable } from "./PlatformsTable";
import { SourcesSidebar } from "./SourcesSidebar";
import { HealthStrip, PlatformsStrip } from "./SourcesStrip";
import { SOURCE_VISIBILITY, SourcesTable, VALUE_COLUMNS } from "./SourcesTable";
import { SourcesToolbar, sourceChips } from "./SourcesToolbar";
import { SourceBulkDialog, SourcesBulkBar } from "./SourcesBulk";
import { CoverageKey } from "./SourceVisuals";
import { useSourceSelection, type SourceSelection } from "./bulk";

function safeJson(text: string | null): unknown {
  try {
    return text === null ? [] : JSON.parse(text);
  } catch {
    return [];
  }
}

const WINDOW_DAYS: Partial<Record<string, 30 | 180>> = { "30": 30, "180": 180 };

type Toolbar = (count: ReactNode, tools: ReactNode) => ReactNode;
type Banner = SourcesResponse["banners"][number];

export interface SourcesViewProps {
  search: SourcesSearch;
  sourceId: string | null;
  gapId?: string | null;
  adding: boolean;
  onSearch: (next: SourcesSearch) => void;
  onOpenSource: (id: string | null, search?: SourcesSearch) => void;
  onAdd: (open: boolean) => void;
}

function defaultTab(sourceId: string | null, gapId: string | null): NonNullable<SourcesSearch["tab"]> {
  if (gapId) return "gaps";
  return sourceId ? "sources" : "platforms";
}

// DI-114: a gap link doesn't say whether the gap is still open, so a gap missing from the open list is looked for
// once among the resolved ones.
function useResolvedGapFallback(
  gapId: string | null,
  data: SourcesResponse | undefined,
  search: SourcesSearch,
  onSearch: (next: SourcesSearch) => void,
) {
  const tried = useRef(false);
  useEffect(() => {
    if (!gapId || !data || tried.current || search.gaps === "resolved") return;
    if (data.gaps.some((g) => g.id === gapId)) return;
    tried.current = true;
    onSearch({ ...search, tab: "gaps", gaps: "resolved" });
  }, [gapId, data, search, onSearch]);
}

interface TableState {
  readonly visibility: ColumnVisibility;
  readonly selection: SourceSelection;
  readonly values: ReadonlyMap<string, SourceValueRow> | null;
  readonly onBulk: (action: SourceBulkAction) => void;
}

type SourceValueRow = SourceValues["sources"][number];

// DI-114: the list keeps checked rows and chosen columns here, above the tabs and the drawer, so neither resets them.
function useTableState(data: SourcesResponse | undefined) {
  const visibility = useColumnVisibility(SOURCE_VISIBILITY);
  const selection = useSourceSelection(data?.sources ?? []);
  const [bulk, setBulk] = useState<SourceBulkAction | null>(null);
  const valuesShown = VALUE_COLUMNS.some((id) => visibility.isVisible(id));
  const valuesQuery = useQuery({ ...sourceValuesQuery, enabled: valuesShown });
  const values = valuesQuery.data ? new Map(valuesQuery.data.sources.map((v) => [v.source_id, v])) : null;
  const table: TableState = { visibility, selection, values, onBulk: setBulk };
  const dialog = bulk ? (
    <SourceBulkDialog
      action={bulk}
      ids={[...selection.picked]}
      onClose={() => {
        setBulk(null);
      }}
      onDone={selection.clear}
    />
  ) : null;
  return { table, dialog };
}

function useBanners(data: SourcesResponse | undefined) {
  const [dismissed, setDismissed] = useState<Set<string>>(
    () => new Set(dismissedBannersSchema.parse(safeJson(window.sessionStorage.getItem("amber.sources.banners")))),
  );
  const banners = (data?.banners ?? []).filter((b) => !dismissed.has(b.key));
  const dismiss = (key: string) => {
    const next = new Set([...dismissed, key]);
    setDismissed(next);
    window.sessionStorage.setItem("amber.sources.banners", JSON.stringify([...next]));
  };
  return { banners, dismiss };
}

function AddSourceButton({ data, onAdd }: { data: SourcesResponse | undefined; onAdd: (open: boolean) => void }) {
  return (
    <Button
      size="sm"
      disabledReason={data && !data.actions.add_source.enabled ? data.actions.add_source.reason : null}
      onClick={() => {
        onAdd(true);
      }}
    >
      <Plus aria-hidden="true" />
      Add source…
    </Button>
  );
}

function useEditing(sourceId: string | null, onOpenSource: (id: string | null) => void) {
  const [editing, setEditing] = useState<string | null>(null);
  return {
    editing: editing !== null && editing === sourceId,
    edit: (id: string) => {
      setEditing(id);
      if (id !== sourceId) onOpenSource(id);
    },
    setOpen: (open: boolean) => {
      setEditing(open ? sourceId : null);
    },
  };
}

export function SourcesView({
  search,
  sourceId,
  gapId = null,
  adding,
  onSearch,
  onOpenSource,
  onAdd,
}: SourcesViewProps) {
  const query = useQuery(sourcesQuery({ ...search, ...(gapId ? { tab: "gaps" as const } : {}) }));
  const data = query.data;
  useResolvedGapFallback(gapId, data, search, onSearch);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const tab = search.tab ?? defaultTab(sourceId, gapId);
  const { table, dialog } = useTableState(data);
  const [grouped, setGrouped] = useState(true);
  const editor = useEditing(sourceId, onOpenSource);
  const set = (patch: Partial<SourcesSearch>) => {
    onSearch({ ...search, ...patch });
  };
  const chips = sourceChips(search, data);
  const reset = () => {
    onSearch({ tab: search.tab, window: search.window });
  };
  const { banners, dismiss } = useBanners(data);
  const toolbar: Toolbar = (count, tools) => (
    <SourcesToolbar
      count={count}
      tools={tools}
      chips={chips}
      filtersOpen={filtersOpen}
      onFilters={() => {
        setFiltersOpen(!filtersOpen);
      }}
      set={set}
      onReset={reset}
    />
  );
  const addButton = <AddSourceButton data={data} onAdd={onAdd} />;
  return (
    <div className="relative flex min-h-0 flex-1">
      <SourcesSidebar open={filtersOpen} data={data} search={search} set={set} />
      <section aria-labelledby="view-title" className="flex min-h-0 min-w-0 flex-1 flex-col">
        <h1 id="view-title" className="sr-only">
          Sources
        </h1>
        <TabList
          label="Sources view"
          value={tab}
          items={[
            { value: "platforms", label: "Platforms", ...(data ? { count: data.platforms.length } : {}) },
            { value: "sources", label: "Sources", ...(data ? { count: data.sources.length } : {}) },
            { value: "gaps", label: "Gaps", ...(data ? { count: data.counts.open_gaps } : {}) },
          ]}
          onChange={(value) => {
            set({ tab: value, ...(value === "gaps" ? {} : { gaps: undefined }) });
          }}
        >
          {sourceId || adding ? null : (
            <p className="-mt-[46px] mb-[22px] self-end pr-4 text-md text-muted max-[1180px]:hidden">
              A complete scan or an empty result does not prove nothing was missed.
            </p>
          )}
          {query.isError ? (
            <Callout tone="attention" role="alert" className="mx-4 mb-2">
              Couldn&apos;t load sources. {errorText(query.error)}{" "}
              <LinkButton
                onClick={() => {
                  void query.refetch();
                }}
              >
                Retry
              </LinkButton>
            </Callout>
          ) : null}
          <TabPanel value="platforms">
            {data ? (
              <PlatformsPanel
                data={data}
                toolbar={toolbar}
                addButton={addButton}
                onOpen={(platform) => {
                  onSearch({ ...search, tab: "sources", platform });
                }}
              />
            ) : (
              <SkeletonTable />
            )}
          </TabPanel>
          <TabPanel value="sources">
            <SourcesPanel
              data={data}
              search={search}
              set={set}
              filtered={chips.length > 0}
              banners={banners}
              onDismiss={dismiss}
              toolbar={toolbar}
              addButton={addButton}
              grouped={grouped}
              onGrouped={setGrouped}
              sourceId={sourceId}
              onOpenSource={onOpenSource}
              onSearch={onSearch}
              table={table}
              onEdit={editor.edit}
            />
          </TabPanel>
          <TabPanel value="gaps">
            {data ? (
              <GapsPanel
                data={data}
                search={search}
                set={set}
                toolbar={toolbar}
                gapId={gapId}
                onOpenSource={onOpenSource}
              />
            ) : (
              <SkeletonTable />
            )}
          </TabPanel>
        </TabList>
      </section>
      {dialog}
      <SourceSidePanel
        search={search}
        sourceId={sourceId}
        adding={adding}
        editing={editor.editing}
        onSearch={onSearch}
        onOpenSource={onOpenSource}
        onAdd={onAdd}
        onEditing={editor.setOpen}
      />
    </div>
  );
}

function SourceSidePanel({
  search,
  sourceId,
  adding,
  editing,
  onSearch,
  onOpenSource,
  onAdd,
  onEditing,
}: SourcesViewProps & { editing: boolean; onEditing: (open: boolean) => void }) {
  if (adding) {
    return (
      <AddSourcePanel
        onClose={() => {
          onAdd(false);
        }}
        onAdded={(id) => {
          onAdd(false);
          onOpenSource(id, { ...search, tab: "sources" });
        }}
      />
    );
  }
  if (!sourceId) return null;
  if (editing)
    return (
      <EditSourcePanel
        id={sourceId}
        onClose={() => {
          onEditing(false);
        }}
      />
    );
  return (
    <SourceDrawer
      id={sourceId}
      onEdit={() => {
        onEditing(true);
      }}
      onClose={() => {
        onOpenSource(null);
      }}
      onGaps={() => {
        onSearch({ ...search, tab: "gaps", source: sourceId });
      }}
    />
  );
}

function PlatformsPanel({
  data,
  toolbar,
  addButton,
  onOpen,
}: {
  data: SourcesResponse;
  toolbar: Toolbar;
  addButton: ReactNode;
  onOpen: (platform: string) => void;
}) {
  return (
    <>
      <PlatformsStrip data={data} />
      {toolbar(
        <>
          All <b className="font-semibold text-fg">{data.platforms.length}</b> platforms
        </>,
        addButton,
      )}
      <PlatformsTable data={data} onOpen={onOpen} />
    </>
  );
}

function SourcesPanel({
  data,
  search,
  set,
  filtered,
  banners,
  onDismiss,
  toolbar,
  addButton,
  grouped,
  onGrouped,
  sourceId,
  onOpenSource,
  onSearch,
  table,
  onEdit,
}: {
  data: SourcesResponse | undefined;
  search: SourcesSearch;
  set: (patch: Partial<SourcesSearch>) => void;
  filtered: boolean;
  table: TableState;
  banners: readonly Banner[];
  onDismiss: (key: string) => void;
  toolbar: Toolbar;
  addButton: ReactNode;
  grouped: boolean;
  onGrouped: (grouped: boolean) => void;
  sourceId: string | null;
  onOpenSource: (id: string | null, search?: SourcesSearch) => void;
  onSearch: (next: SourcesSearch) => void;
  onEdit: (id: string) => void;
}) {
  const windowControl = (
    <SegmentedFilter
      label="Coverage window"
      value={String(search.window ?? 90)}
      options={[
        { value: "30", label: "30 d" },
        { value: "90", label: "90 d" },
        { value: "180", label: "180 d" },
      ]}
      onChange={(v) => {
        set({ window: WINDOW_DAYS[v] });
      }}
    />
  );
  return (
    <>
      {banners.map((b) => (
        <Callout key={b.key} tone="waiting" className="mx-4 mb-2 flex items-center gap-2">
          <span className="flex-1">{b.text}</span>
          <LinkButton
            onClick={() => {
              set({ tab: "platforms" });
            }}
          >
            View on Platforms
          </LinkButton>
          <IconButton
            label="Dismiss"
            size="sm"
            onClick={() => {
              onDismiss(b.key);
            }}
          >
            <X size={13} aria-hidden="true" />
          </IconButton>
        </Callout>
      ))}
      {data?.counts.sources === 0 ? (
        <EmptyState icon={<Radio />} title="No sources yet" actions={addButton}>
          Sources appear here when they are added to a person&apos;s plan or monitored as shared sources.
        </EmptyState>
      ) : null}
      {data && data.counts.sources !== 0 ? (
        <>
          <HealthStrip
            data={data}
            search={search}
            set={set}
            gapsOn={false}
            onGaps={() => {
              set({ tab: "gaps" });
            }}
          />
          {toolbar(
            filtered ? (
              `Showing ${String(data.sources.length)} of ${String(data.counts.sources)}`
            ) : (
              <>
                All <b className="font-semibold text-fg">{data.counts.sources}</b> sources
              </>
            ),
            <>
              {addButton}
              {windowControl}
              <CoverageKey />
              <Switch checked={grouped} label="Group by health" onChange={onGrouped} />
              <ColumnsMenu columns={table.visibility.toggles} />
            </>,
          )}
          <SourcesBulkBar
            rows={data.sources}
            selection={table.selection}
            actions={data.actions}
            onOpen={table.onBulk}
          />
          <SourcesTable
            data={data}
            rows={data.sources}
            selected={sourceId}
            grouped={grouped}
            hidden={table.visibility.hidden}
            values={table.values}
            selection={table.selection}
            onOpen={(id) => {
              onOpenSource(id);
            }}
            onGaps={(id) => {
              onSearch({ ...search, tab: "gaps", source: id });
            }}
            onEdit={onEdit}
          />
        </>
      ) : null}
      {data ? null : <SkeletonTable />}
    </>
  );
}

function GapsPanel({
  data,
  search,
  set,
  toolbar,
  gapId,
  onOpenSource,
}: {
  data: SourcesResponse;
  search: SourcesSearch;
  set: (patch: Partial<SourcesSearch>) => void;
  toolbar: Toolbar;
  gapId: string | null;
  onOpenSource: (id: string | null, search?: SourcesSearch) => void;
}) {
  return (
    <>
      <HealthStrip data={data} search={search} set={set} gapsOn onGaps={() => undefined} />
      {toolbar(
        search.gaps === "resolved" ? (
          <>
            <b className="font-semibold text-fg">{data.gaps.length}</b> gaps resolved in the last 30 days
          </>
        ) : (
          <>
            All <b className="font-semibold text-fg">{data.gaps.length}</b> open gaps
          </>
        ),
        <SegmentedFilter
          label="Gap state"
          value={search.gaps ?? "open"}
          options={[
            { value: "open", label: `Open ${String(data.counts.open_gaps)}` },
            { value: "resolved", label: `Resolved · 30 d ${String(data.counts.resolved_gaps_30d)}` },
          ]}
          onChange={(v) => {
            set({ gaps: v === "resolved" ? "resolved" : undefined });
          }}
        />,
      )}
      <GapsTable
        gaps={data.gaps}
        actions={data.actions}
        resolved={search.gaps === "resolved"}
        selectedId={gapId}
        onSource={(id) => {
          onOpenSource(id, { ...search, tab: "sources" });
        }}
      />
    </>
  );
}

function SkeletonTable() {
  return (
    <GridWrap>
      <Grid minWidth={600} aria-label="Loading">
        <tbody>
          <SkeletonRows columns={6} />
        </tbody>
      </Grid>
    </GridWrap>
  );
}

export function SourcesStatus() {
  const query = useQuery(sourcesQuery({}));
  const data = query.data;
  if (!data) return null;
  const attention =
    (data.counts.by_health["failing"] ?? 0) +
    (data.counts.by_health["partial"] ?? 0) +
    (data.counts.by_health["late"] ?? 0);
  return (
    <span className="max-narrow:hidden">
      <b className="font-strong text-fg">{data.counts.scanning.length}</b> scanning ·{" "}
      <b className="font-strong text-fg">{attention}</b> need attention ·{" "}
      <b className="font-strong text-fg">{data.counts.open_gaps}</b> open gaps
      {data.counts.next_scan_at ? ` · Next scheduled scan ${formatRelative(data.counts.next_scan_at)}` : ""} ·{" "}
      {data.counts.sources} sources
    </span>
  );
}
